import { useState } from "react";
import { AccountState, RiskState, api, isError } from "../api";

function fmtCcy(v: number, ccy: string) {
  return `${v >= 0 ? "+" : ""}${v.toFixed(2)} ${ccy}`;
}

function dailyPctOf(equity: number, starting: number): number {
  if (starting <= 0) return 0;
  return ((equity - starting) / starting) * 100;
}

interface Props {
  risk: RiskState | null;
  riskError: string | null;
  account: AccountState | null;
  onChange: () => void;
}

export function RiskPanel({ risk, riskError, account, onChange }: Props) {
  const [busy, setBusy] = useState<string | null>(null);
  const [lastMessage, setLastMessage] = useState<string | null>(null);

  async function onKill() {
    const reason = window.prompt(
      "Reason for arming the kill switch?",
      "manual halt",
    );
    if (reason === null) return;
    setBusy("kill");
    const r = await api.killSwitch(reason || "manual");
    setBusy(null);
    setLastMessage(isError(r) ? `Kill failed: ${r.error}` : "Kill switch armed");
    onChange();
  }

  async function onArm() {
    if (!window.confirm("Clear the kill switch and allow new trades?")) return;
    setBusy("arm");
    const r = await api.armRisk();
    setBusy(null);
    setLastMessage(isError(r) ? `Arm failed: ${r.error}` : "Kill switch cleared");
    onChange();
  }

  async function onCloseAll() {
    if (
      !window.confirm(
        "Close ALL open positions immediately? This cannot be undone.",
      )
    )
      return;
    setBusy("close");
    const r = await api.closeAll();
    setBusy(null);
    if (isError(r)) {
      setLastMessage(`Close all failed: ${r.error}`);
    } else {
      setLastMessage(
        `Close all: ${r.closed.length} of ${r.requested} closed` +
          (r.failed.length ? `, ${r.failed.length} failed` : ""),
      );
    }
    onChange();
  }

  const active = risk?.kill_switch.active === true;
  const starting = risk?.today?.starting_equity ?? null;
  const equity = account?.equity ?? null;
  const ccy = account?.currency ?? "USD";
  const pnl = starting !== null && equity !== null ? equity - starting : null;
  const pnlPct =
    starting !== null && equity !== null ? dailyPctOf(equity, starting) : null;
  const limit = risk?.limits.daily_loss_limit_pct ?? 0;
  const breached = pnlPct !== null && pnlPct <= -limit;
  const warning =
    pnlPct !== null && pnlPct < 0 && !breached && Math.abs(pnlPct) >= limit / 2;
  const realisedPnl = risk?.today?.realised_pnl ?? null;
  const closedCount = risk?.today?.closed_count ?? 0;

  return (
    <section className="bg-surface border border-border rounded p-4 h-full">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm uppercase tracking-wider text-fg-muted">Risk</h2>
        {risk && (
          <span className="text-xs text-fg-muted font-mono">
            limit {risk.limits.daily_loss_limit_pct}% · per-trade{" "}
            {risk.limits.risk_per_trade_pct}%
          </span>
        )}
      </div>

      {riskError && <p className="text-danger text-sm">{riskError}</p>}

      <div className="space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="text-fg-muted">Today P/L</span>
          <span
            className={`font-mono ${
              pnl === null
                ? "text-fg-muted"
                : pnl >= 0
                  ? "text-success"
                  : breached
                    ? "text-danger font-bold"
                    : warning
                      ? "text-warn"
                      : "text-fg"
            }`}
          >
            {pnl === null
              ? "—"
              : `${fmtCcy(pnl, ccy)} (${pnlPct! >= 0 ? "+" : ""}${pnlPct!.toFixed(2)}%)`}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-fg-muted">Starting equity</span>
          <span className="font-mono">
            {starting !== null ? `${starting.toFixed(2)} ${ccy}` : "—"}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-fg-muted">Realised today</span>
          <span
            className={`font-mono ${
              realisedPnl === null
                ? "text-fg-muted"
                : realisedPnl > 0
                  ? "text-success"
                  : realisedPnl < 0
                    ? "text-danger"
                    : "text-fg"
            }`}
          >
            {realisedPnl === null
              ? "—"
              : `${realisedPnl >= 0 ? "+" : ""}${realisedPnl.toFixed(2)} ${ccy} (${closedCount} closed)`}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-fg-muted">Kill switch</span>
          <span
            className={`font-mono font-bold uppercase ${active ? "text-danger" : "text-success"}`}
          >
            {active ? "armed" : "off"}
          </span>
        </div>
        {active && risk?.kill_switch.reason && (
          <p className="text-xs text-fg-muted italic">
            reason: {risk.kill_switch.reason}
          </p>
        )}
      </div>

      <div className="mt-4 flex flex-col gap-2">
        {active ? (
          <button
            onClick={onArm}
            disabled={busy !== null}
            className="px-3 py-2 rounded bg-success/20 border border-success text-success font-bold uppercase text-sm disabled:opacity-50"
          >
            {busy === "arm" ? "clearing…" : "clear kill switch"}
          </button>
        ) : (
          <button
            onClick={onKill}
            disabled={busy !== null}
            className="px-3 py-2 rounded bg-danger/20 border border-danger text-danger font-bold uppercase text-sm disabled:opacity-50"
          >
            {busy === "kill" ? "arming…" : "arm kill switch"}
          </button>
        )}
        <button
          onClick={onCloseAll}
          disabled={busy !== null}
          className="px-3 py-2 rounded bg-surface-2 border border-border text-fg font-bold uppercase text-sm disabled:opacity-50 hover:bg-bg-hover"
        >
          {busy === "close" ? "closing…" : "close all positions"}
        </button>
        {lastMessage && (
          <p className="text-xs text-fg-muted font-mono">{lastMessage}</p>
        )}
      </div>
    </section>
  );
}
